import { Fragment } from 'react';
import { PLAYBACK_MS } from '@roborally/engine';
import { formatDuration } from '../duration.js';
import type { CourseLayout, GameSettings as Settings } from '../api.js';

/** The same settings as one line, for a list of games. */
export function settingsLine(s: Settings): string {
  return [
    s.turnMode ? 'turn mode' : 'normal mode',
    s.lifeTokens > 0 ? `${s.lifeTokens} lives` : 'no lives',
    !s.optionsEnabled
      ? 'no options'
      : `${s.optionCount === null ? `all ${s.optionCatalog}` : `${s.optionCount} of ${s.optionCatalog}`} options, ${s.startingOptions} to start`,
    s.debrisEnabled ? 'debris' : 'no debris',
    s.lateJoin ? 'late joining' : 'closed once started',
    ...(s.turnMode ? [s.botsAllowed ? 'bots allowed' : 'no bots'] : []),
    s.timerSeconds === null
      ? 'no timer'
      : `${formatDuration(s.timerSeconds)} timer${s.timerMode === 'last' ? ' for the last player' : ''}`,
    ...(s.playbackMs !== PLAYBACK_MS ? [`${s.playbackMs} ms playback`] : []),
  ].join(' / ');
}

/**
 * How the host set the game up, under the key to the board.
 *
 * The labels are the setup screen's own, so a setting reads as the
 * question the host was asked. Every row says which way it went, off
 * included: a player checking for debris is not helped by a missing row.
 * `boards` are the course's, each with how far it is turned, and `flags`
 * how many it is played to.
 */
export function GameSettings({ settings, boards, flags }: {
  settings: Settings;
  boards?: CourseLayout['boards'];
  flags?: number;
}) {
  const s = settings;
  const rows: [string, string][] = [
    ['Game mode', s.turnMode ? 'turn' : 'normal'],
    ...(boards?.length
      ? [['Board Name', boards.map((b) => `${b.id} (${b.rotation}\u00B0)`).join(', ')] as [string, string]]
      : []),
    ...(flags ? [['Flags', String(flags)] as [string, string]] : []),
    ['Maximum players', String(s.maxPlayers)],
    ['Life tokens', s.lifeTokens > 0 ? `${s.lifeTokens} each` : 'off: deaths are only counted'],
    ['Option cards', !s.optionsEnabled
      ? 'off'
      : s.optionCount === null
        ? `all ${s.optionCatalog}`
        : `${s.optionCount} of ${s.optionCatalog}`],
  ];
  if (s.optionsEnabled) {
    rows.push(['Options to start with', String(s.startingOptions)]);
  }
  rows.push(
    ['Debris', s.debrisEnabled ? 'on' : 'off'],
    ['Joining', s.lateJoin ? 'open after the start' : 'closed once started'],
    ...(s.turnMode ? [['Bots', s.botsAllowed ? 'may join' : 'not allowed'] as [string, string]] : []),
    ['Programming timer', s.timerSeconds === null
      ? 'off'
      : s.timerMode === 'last'
        ? `${formatDuration(s.timerSeconds)} for the last player`
        : `${formatDuration(s.timerSeconds)} for the whole table`],
    ['Playback step', `${s.playbackMs} ms`],
  );

  // Two settings to a row, so the table is four columns rather than a
  // long two-column list.
  const pairs: [string, string][][] = [];
  for (let i = 0; i < rows.length; i += 2) pairs.push(rows.slice(i, i + 2));

  return (
    <section className="game-settings" aria-label="Game settings">
      <div className="hazard hazard-thin" role="presentation" />
      <table>
        <tbody>
          {pairs.map((pair) => (
            <tr key={pair[0][0]}>
              {pair.map(([label, value]) => (
                <Fragment key={label}>
                  <th scope="row">{label}</th>
                  <td>{value}</td>
                </Fragment>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
